import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

export const reportViewerRoles = [
  "analytics.admin",
  "analytics.viewer",
  "crm.admin",
  "crm.supervisor",
  "crm.bi-reader",
] as const;

export const reportEditorRoles = ["analytics.admin", "crm.admin"] as const;

export const reportCellWidths = ["full", "half", "third", "quarter"] as const;

export type ReportCellWidth = (typeof reportCellWidths)[number];

export const reportCellSerializer = Serializers.object({
  uuid: uuidSerializer,
  title: Serializers.string,
  width: Serializers.stringOneOf(...reportCellWidths),
  visible: Serializers.boolean,
  code: Serializers.string,
});

export type ReportCell = SerializerValue<typeof reportCellSerializer>;

export const reportSerializer = Serializers.object({
  uuid: uuidSerializer,
  title: Serializers.string,
  description: Serializers.string,
  cells: reportCellSerializer.array(),
});

export type Report = SerializerValue<typeof reportSerializer>;

export const reportMediaSerializer = Serializers.object({
  type: Serializers.stringOneOf("text/html", "image/png"),
  data: Serializers.string,
});

export type ReportMedia = SerializerValue<typeof reportMediaSerializer>;

export const reportCellResultSerializer = Serializers.object({
  uuid: uuidSerializer,
  status: Serializers.stringOneOf("ok", "error"),
  error: Serializers.string.nullable(),
  stdout: Serializers.string,
  stderr: Serializers.string,
  columns: Serializers.string.array(),
  rows: Serializers.identity<unknown[][]>(),
  media: reportMediaSerializer.array(),
});

export type ReportCellResult = SerializerValue<typeof reportCellResultSerializer>;

export const reports = {
  reportsQuery: HttpAction({
    path: "/reports",
    method: "get",
    qsSerializer: Serializers.object({
      searchText: Serializers.string,
      limit: Serializers.integer,
      offset: Serializers.integer,
    }).partial(),
    requiresAuth: true as const,
    valueSerializer: Serializers.object({
      items: reportSerializer.array(),
      count: Serializers.integer,
    }),
  }),

  reportQuery: HttpAction({
    path: "/report/:uuid",
    method: "get",
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
    requiresAuth: true as const,
    valueSerializer: reportSerializer,
    errorSerializer: Serializers.stringOneOf("ReportNotFound"),
  }),

  reportOutputsQuery: HttpAction({
    path: "/report/:uuid/outputs",
    method: "get",
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
    qsSerializer: Serializers.object({
      start: Serializers.string,
      end: Serializers.string,
    }),
    requiresAuth: true as const,
    valueSerializer: reportCellResultSerializer.array(),
    errorSerializer: Serializers.stringOneOf("ReportNotFound", "ReportRunFailed"),
  }),

  saveReport: HttpAction({
    path: "/report",
    method: "put",
    requiresAuth: true as const,
    bodySerializer: reportSerializer,
    valueSerializer: reportSerializer,
  }),

  removeReport: HttpAction({
    path: "/report/:uuid",
    method: "delete",
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
    requiresAuth: true as const,
  }),
};
